import React, { useState, useMemo } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { toast } from "sonner";
import { Plus, Trash2, TrainFront, ClipboardCheck, AlertTriangle } from "lucide-react";
import { DEST_CODES, EMPTY_DEST, destLabel } from "../lib/destinations";
import { trackLabel, cargoLabel, statusLabel, stationName } from "../i18n";
import { synthesizeBogies, hasBogieImbalance } from "../lib/bogie";

const TARE = 23;
const genNum = () => `6${Math.floor(Math.random() * 10)}${Math.floor(100000 + Math.random() * 900000)}`;

function rowBrutto(r) {
  if (r.cargo === "empty") {
    const b = r.brutto != null && r.brutto !== "" ? +r.brutto : TARE;
    return +Number(b).toFixed(1);
  }
  const netto = +r.netto || 0;
  if (r.brutto != null && r.brutto !== "") return +Number(r.brutto).toFixed(1);
  return +(netto + TARE).toFixed(1);
}

function newRow() {
  const netto = "65.0";
  const brutto = +(parseFloat(netto) + TARE).toFixed(1);
  const bogies = synthesizeBogies(brutto);
  return {
    number: genNum(),
    cargo: "coal",
    netto,
    brutto: String(brutto),
    destination: DEST_CODES[0],
    status: "arriving",
    bogie1_t: bogies.bogie1_t,
    bogie2_t: bogies.bogie2_t,
  };
}

function refreshBogies(row) {
  const brutto = rowBrutto(row);
  const bogies = synthesizeBogies(brutto, { empty: row.cargo === "empty" });
  return { ...row, brutto: String(brutto), bogie1_t: bogies.bogie1_t, bogie2_t: bogies.bogie2_t };
}

export const WorkerPanel = ({ yard, t, lang, onAcceptTrain, compact }) => {
  const [stCode, setStCode] = useState(yard.stations[0]?.code || "");
  const [track, setTrack] = useState("0");
  const [trainNo, setTrainNo] = useState("");
  const [rows, setRows] = useState([newRow(), newRow(), newRow()]);
  const station = useMemo(
    () => yard.stations.find((s) => s.code === stCode) || yard.stations[0],
    [yard, stCode]
  );
  const trackObj = station?.tracks[parseInt(track, 10)] || station?.tracks[0];
  const free = trackObj ? trackObj.capacity - trackObj.count : 0;

  const validRows = rows.filter((r) => r.number.trim().length >= 4);
  const hasTrainNo = trainNo.trim().length > 0;
  const isValid = hasTrainNo && validRows.length > 0;

  const summary = useMemo(() => {
    let brutto = 0;
    let netto = 0;
    const dests = new Set();
    validRows.forEach((r) => {
      brutto += rowBrutto(r);
      netto += r.cargo === "empty" ? 0 : +r.netto || 0;
      if (r.cargo !== "empty" && r.destination && r.destination !== EMPTY_DEST) {
        dests.add(destLabel(r.destination, lang));
      }
    });
    return {
      count: validRows.length,
      brutto: +brutto.toFixed(1),
      netto: +netto.toFixed(1),
      destinations: [...dests],
    };
  }, [validRows, lang]);

  const validationHint = (() => {
    if (isValid) return null;
    const parts = [];
    if (!hasTrainNo) parts.push(t("hintTrainNoRequired"));
    if (validRows.length === 0) parts.push(t("hintWagonRequired"));
    return parts.join(" · ");
  })();

  const isInvalidWagonNo = (num) => {
    const trimmed = num.trim();
    return trimmed.length > 0 && trimmed.length < 4;
  };

  const setRow = (i, k, v) =>
    setRows((rs) =>
      rs.map((r, j) => {
        if (j !== i) return r;
        let next = { ...r, [k]: v };
        if (k === "cargo") {
          if (v === "empty") {
            next = { ...next, netto: "0", destination: EMPTY_DEST, brutto: String(TARE) };
          } else if (r.cargo === "empty") {
            next = { ...next, netto: "65.0", destination: DEST_CODES[0] };
            next.brutto = String(+(parseFloat(next.netto) + TARE).toFixed(1));
          }
          next = refreshBogies(next);
        } else if (k === "netto") {
          const netto = v === "" ? 0 : +v;
          next.brutto = String(+(netto + TARE).toFixed(1));
          next = refreshBogies(next);
        } else if (k === "brutto") {
          next = refreshBogies(next);
        }
        return next;
      })
    );

  const submit = () => {
    if (!isValid) return;
    const payload = validRows.map((r) => ({
      ...r,
      brutto: rowBrutto(r),
      netto: r.cargo === "empty" ? 0 : r.netto,
    }));
    const res = onAcceptTrain(stCode, parseInt(track, 10), trainNo.trim(), payload);
    if (!res.ok) toast.error(t("noSpaceTrack"));
    else if (res.accepted < validRows.length) toast.warning(`${t("partialAccept")} (${res.accepted}/${validRows.length})`);
    else toast.success(`${t("acceptedTrain")} · ${res.accepted} ${t("wagonsOnTrack")}`);
    if (res.ok) {
      setTrainNo("");
      setRows([newRow(), newRow(), newRow()]);
    }
  };

  const stationLabel = (s) => stationName(s.code, lang);

  return (
    <div className={`h-full overflow-y-auto no-scrollbar space-y-4 ${compact ? "p-3" : "p-4"}`} data-testid="worker-panel">
      <div className="flex items-center gap-2 text-amber-400">
        <TrainFront size={16} />
        <span className="text-[12px] font-semibold uppercase tracking-widest">{t("intake")}</span>
      </div>

      <div className="space-y-1">
        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{t("trainNo")}</Label>
        <Input
          data-testid="intake-train-no"
          value={trainNo}
          onChange={(e) => setTrainNo(e.target.value)}
          placeholder="№ 2401"
          className="bg-zinc-900 border-zinc-800 h-11 font-mono min-h-[44px]"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{t("intakeStation")}</Label>
          <Select value={stCode} onValueChange={(v) => { setStCode(v); setTrack("0"); }}>
            <SelectTrigger data-testid="intake-station" className="bg-zinc-900 border-zinc-800 h-11 min-h-[44px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yard.stations.map((s) => (
                <SelectItem key={s.code} value={s.code}>{stationLabel(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{t("intakeTrack")}</Label>
          <Select value={track} onValueChange={setTrack}>
            <SelectTrigger data-testid="intake-track" className="bg-zinc-900 border-zinc-800 h-11 min-h-[44px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {station?.tracks.map((tr, i) => (
                <SelectItem key={tr.id} value={String(i)}>
                  {trackLabel(tr.idx, t)} · {tr.capacity - tr.count} {t("freeSlots")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{t("wagonList")}</Label>
          <span className={`text-[10px] font-mono ${free < rows.length ? "text-rose-400" : "text-zinc-500"}`}>
            {rows.length} / {free} {t("freeSlots")}
          </span>
        </div>

        {/* compact column headers */}
        <div
          className={`grid gap-1 px-2 text-[9px] uppercase tracking-wider text-zinc-600 font-mono ${
            compact ? "grid-cols-[1fr_44px_44px]" : "grid-cols-[1fr_52px_52px_1fr_72px_28px]"
          }`}
        >
          <span>{t("numberShort")}</span>
          <span className="text-right">{t("bruttoShort")}</span>
          <span className="text-right">{t("nettoShort")}</span>
          {!compact && (
            <>
              <span>{t("destShort")}</span>
              <span>{t("status")}</span>
              <span />
            </>
          )}
        </div>

        {rows.map((r, i) => {
          const skewed = hasBogieImbalance({
            bogie1_t: r.bogie1_t,
            bogie2_t: r.bogie2_t,
            weight_brutto: rowBrutto(r),
          });
          return (
            <div
              key={i}
              className={`bg-zinc-900/60 border rounded-sm p-2 space-y-2 ${
                skewed ? "border-orange-500/50" : "border-zinc-800"
              }`}
              data-testid={`intake-row-${i}`}
            >
              <div className={`grid gap-1.5 items-center ${compact ? "grid-cols-[1fr_44px_44px_36px]" : "grid-cols-[1fr_52px_52px_1fr_72px_36px]"}`}>
                <div className="flex items-center gap-1 min-w-0">
                  <span className="text-[10px] font-mono text-zinc-600 w-3 shrink-0">{i + 1}</span>
                  {skewed && (
                    <AlertTriangle size={12} className="text-orange-400 shrink-0" title={t("bogieImbalanceWarn")} />
                  )}
                  <Input
                    data-testid={`intake-row-number-${i}`}
                    value={r.number}
                    onChange={(e) => setRow(i, "number", e.target.value)}
                    className={`bg-zinc-950 border-zinc-800 h-9 font-mono text-[11px] min-w-0 ${
                      isInvalidWagonNo(r.number) ? "border-amber-600/50 focus-visible:ring-amber-600/30" : ""
                    }`}
                  />
                </div>
                <Input
                  data-testid={`intake-row-brutto-${i}`}
                  type="number"
                  value={r.cargo === "empty" ? rowBrutto(r) : r.brutto}
                  onChange={(e) => setRow(i, "brutto", e.target.value)}
                  className="bg-zinc-950 border-zinc-800 h-9 font-mono text-[11px] px-1 text-right"
                />
                <Input
                  data-testid={`intake-row-netto-${i}`}
                  type="number"
                  disabled={r.cargo === "empty"}
                  value={r.cargo === "empty" ? "" : r.netto}
                  placeholder="—"
                  onChange={(e) => setRow(i, "netto", e.target.value)}
                  className="bg-zinc-950 border-zinc-800 h-9 font-mono text-[11px] px-1 text-right"
                />
                {!compact && (
                  <>
                    <Select
                      value={r.cargo === "empty" ? EMPTY_DEST : r.destination}
                      onValueChange={(v) => setRow(i, "destination", v)}
                      disabled={r.cargo === "empty"}
                    >
                      <SelectTrigger data-testid={`intake-row-dest-${i}`} className="bg-zinc-950 border-zinc-800 h-9 text-[10px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEST_CODES.map((d) => (
                          <SelectItem key={d} value={d}>{destLabel(d, lang)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={r.cargo} onValueChange={(v) => setRow(i, "cargo", v)}>
                      <SelectTrigger data-testid={`intake-row-cargo-${i}`} className="bg-zinc-950 border-zinc-800 h-9 text-[10px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="coal">{t("coal")}</SelectItem>
                        <SelectItem value="empty">{t("empty")}</SelectItem>
                        <SelectItem value="foreign">{t("foreign")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
                <button
                  type="button"
                  data-testid={`intake-row-remove-${i}`}
                  onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                  className="h-9 w-9 grid place-items-center text-zinc-600 hover:text-rose-400"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {compact && (
                <div className="grid grid-cols-2 gap-2 pl-4">
                  <Select value={r.cargo} onValueChange={(v) => setRow(i, "cargo", v)}>
                    <SelectTrigger data-testid={`intake-row-cargo-${i}`} className="bg-zinc-950 border-zinc-800 h-10 text-[11px] min-h-[40px]">
                      <SelectValue placeholder={t("status")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="coal">{t("coal")}</SelectItem>
                      <SelectItem value="empty">{t("empty")}</SelectItem>
                      <SelectItem value="foreign">{t("foreign")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {r.cargo !== "empty" ? (
                    <Select value={r.destination} onValueChange={(v) => setRow(i, "destination", v)}>
                      <SelectTrigger data-testid={`intake-row-dest-${i}`} className="bg-zinc-950 border-zinc-800 h-10 text-[11px] min-h-[40px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEST_CODES.map((d) => (
                          <SelectItem key={d} value={d}>{destLabel(d, lang)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex items-center px-2 text-[11px] text-zinc-500 font-mono">
                      {statusLabel(r.status || "arriving", t)} · {cargoLabel(r.cargo, t)}
                    </div>
                  )}
                </div>
              )}

              {skewed && (
                <p className="text-[10px] text-orange-400 pl-4" data-testid={`intake-row-skew-${i}`}>
                  {t("bogieImbalanceWarn")}
                </p>
              )}
            </div>
          );
        })}

        {/* train summary */}
        <div
          className="bg-zinc-900/80 border border-zinc-700 rounded-sm px-3 py-2.5 space-y-1"
          data-testid="intake-summary"
        >
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">{t("intakeSummary")}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] font-mono text-zinc-200">
            <span>{summary.count} {t("wagonsOnTrack")}</span>
            <span>Σ {t("bruttoShort")} {summary.brutto} {t("tonneUnit")}</span>
            <span>Σ {t("nettoShort")} {summary.netto} {t("tonneUnit")}</span>
          </div>
          {summary.destinations.length > 0 && (
            <p className="text-[11px] text-zinc-400 truncate" data-testid="intake-summary-dests">
              {t("destShort")}: {summary.destinations.join(", ")}
            </p>
          )}
        </div>

        <Button
          data-testid="intake-add-row"
          onClick={() => setRows((rs) => [...rs, newRow()])}
          disabled={rows.length >= Math.max(free, 1)}
          variant="outline"
          className="w-full h-11 gap-1.5 bg-transparent border-zinc-800 border-dashed text-zinc-500 hover:text-white hover:bg-zinc-900 rounded-sm text-[11px] min-h-[44px]"
        >
          <Plus size={13} /> {t("addRow")}
        </Button>
      </div>

      <Button
        data-testid="intake-submit"
        onClick={submit}
        disabled={!isValid}
        className="w-full h-12 gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-sm min-h-[44px] disabled:opacity-40"
      >
        <ClipboardCheck size={16} /> {t("acceptTrainBtn")}
      </Button>
      {!isValid && validationHint && (
        <p className="text-[12px] text-zinc-500 text-center" data-testid="intake-validation-hint">
          {validationHint}
        </p>
      )}
    </div>
  );
};
